import CategoryIcon from "@mui/icons-material/Category";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { GameSessionModeCategoryContent } from "../../types/api";
import { getLocalizedField } from "../../utils/helpers";

interface SwipeableRevealCardProps {
  isWaiting: boolean;
  isCurrentPlayerImposter: boolean;
  content: GameSessionModeCategoryContent;
  contentImage: string | null;
  imposterModeIcon: string | null;
  swipeOffset: number;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
}

const SwipeableRevealCard: React.FC<SwipeableRevealCardProps> = ({
  isWaiting,
  isCurrentPlayerImposter,
  content,
  contentImage,
  imposterModeIcon,
  swipeOffset,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onMouseDown,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const neonPalette = (theme.palette as any).neon;

  return (
    <Box
      sx={{
        position: "relative",
        mb: 4,
        overflow: "hidden",
        borderRadius: 3,
        cursor: "grab",
        userSelect: "none",
        touchAction: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
    >
      <Paper
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `4px solid ${neonPalette.yellow}`,
          borderRadius: 3,
          boxShadow: `0 8px 32px ${neonPalette.yellow}60, 0 4px 16px ${neonPalette.yellow}30`,
          transform:
            i18n.language === "ar"
              ? `translateX(-${swipeOffset}px)`
              : `translateX(${swipeOffset}px)`,
          transition:
            swipeOffset === 0 ? "transform 0.3s ease" : "none",
          minHeight: 400,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {isWaiting ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              p: 4,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                textAlign: "center",
                background: `linear-gradient(135deg, ${neonPalette.yellow} 0%, ${neonPalette.yellowDark} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("imposter.swipeToReveal")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                color: neonPalette.yellow,
              }}
            >
              {i18n.language === "ar" ? (
                <>
                  <Typography variant="h6">←</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {t("imposter.swipeRight")}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {t("imposter.swipeRight")}
                  </Typography>
                  <Typography variant="h6">→</Typography>
                </>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ width: "100%", p: 3 }}>
            {isCurrentPlayerImposter ? (
              <Paper
                elevation={0}
                sx={{
                  textAlign: "center",
                  p: 4,
                  backgroundColor: neonPalette.pinkLight,
                  borderRadius: 3,
                  border: `4px solid ${neonPalette.pink}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                  boxShadow: `0 8px 32px ${neonPalette.pink}60, 0 4px 16px ${neonPalette.pink}30, inset 0 0 20px ${neonPalette.pinkLight}20`,
                }}
              >
                {imposterModeIcon ? (
                  <Box
                    component="img"
                    src={imposterModeIcon}
                    alt="Imposter"
                    sx={{
                      width: 120,
                      height: 120,
                      objectFit: "contain",
                      filter: `drop-shadow(0 0 10px ${neonPalette.pink}60)`,
                    }}
                  />
                ) : (
                  <CategoryIcon
                    sx={{
                      fontSize: 120,
                      color: neonPalette.pink,
                      filter: `drop-shadow(0 0 10px ${neonPalette.pink}60)`,
                    }}
                  />
                )}
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    color: theme.palette.text.primary,
                    textShadow: `0 0 10px ${neonPalette.pink}40`,
                  }}
                >
                  {t("imposter.youAreImposter")}
                </Typography>
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 3,
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 3,
                  border: `3px solid ${neonPalette.cyan}`,
                  boxShadow: `0 4px 16px ${neonPalette.cyan}40, 0 2px 8px ${neonPalette.cyan}20`,
                  minHeight: 300,
                }}
              >
                {contentImage ? (
                  <Box
                    component="img"
                    src={contentImage}
                    alt={getLocalizedField(content.content)}
                    onError={(e) => {
                      console.error("Image failed to load:", contentImage);
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                    }}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: 500,
                      width: "auto",
                      height: "auto",
                      borderRadius: 2,
                      boxShadow: `0 4px 16px ${neonPalette.cyan}40`,
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 4,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.text.secondary,
                        mb: 2,
                        fontWeight: 600,
                      }}
                    >
                      {t("gameBoard.noImage")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        textAlign: "center",
                      }}
                    >
                      {content?.content.title_ar || content?.content.title}
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SwipeableRevealCard;
